import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { StatusPill } from "@/components/StatusPill";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, MapPin, AlertTriangle, ChevronRight, RotateCw, CloudOff, RefreshCw, Play, Clock, Users, MapPinOff, Crosshair, MessageCircle, Package, Wrench, LogIn, LogOut as LogOutIcon } from "lucide-react";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";
import { loadRouteForDate, startService } from "@/lib/offlineApi";
import api from "@/lib/api";
import { computeServiceTimes, formatTime } from "@/lib/serviceTime";
import { getCurrentLocation, formatDistance } from "@/lib/geolocation";
import { DayTabs } from "@/components/DayTabs";

const TARDY_TIMES = ["15 minutos", "25 minutos", "30 minutos", "45 minutos"];

const normalizePhone = (phone) => {
    const digits = (phone || "").replace(/\D/g, "");
    if (digits.startsWith("503")) return digits;
    if (digits.length === 8) return "503" + digits;
    return digits;
};

const buildTardyMessage = (clientName, techName, time) =>
    `Buen día ${clientName}, 👋🏼 le saluda ${techName}, su técnico asignado de Truly Nolen El Salvador\n\nAntes que nada, le pido una disculpa, 🙏🏼 la visita anterior se extendió y voy con un pequeño retraso para su servicio.\n\nEstoy haciendo lo posible por llegarle cuanto antes y atenderle como usted se merece.\n\n¿Me permite llegar aproximadamente en ${time}?\n\nGracias por su comprensión. 🙂`;

export default function TechRoute() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [routesByDate, setRoutesByDate] = useState({});
    const [clients, setClients] = useState({});
    const [loading, setLoading] = useState(true);
    const [cached, setCached] = useState(false);
    const [startingId, setStartingId] = useState(null);
    const [locating, setLocating] = useState(false);
    const [fallbackOpen, setFallbackOpen] = useState(false);
    const [fallbackSvc, setFallbackSvc] = useState(null);
    const [fallbackErrorMsg, setFallbackErrorMsg] = useState("");
    // "Voy Tarde" state
    const [tardyOpen, setTardyOpen] = useState(false);
    const [tardySvc, setTardySvc] = useState(null);
    const [tardyClient, setTardyClient] = useState(null);
    const [tardyTime, setTardyTime] = useState(TARDY_TIMES[0]);
    const [pendingSupplies, setPendingSupplies] = useState(0);
    const [pendingEquipment, setPendingEquipment] = useState(0);
    const [timeEntry, setTimeEntry] = useState(null);
    const [checkingIn, setCheckingIn] = useState(false);
    const [checkingOut, setCheckingOut] = useState(false);
    const today = format(new Date(), "yyyy-MM-dd");
    const dates = [0, 1, 2].map((i) => format(addDays(new Date(), i), "yyyy-MM-dd"));
    const [selectedDate, setSelectedDate] = useState(today);

    const load = async () => {
        setLoading(true);
        try {
            const results = await Promise.all(dates.map((d) => loadRouteForDate(d)));
            const byDate = {};
            const cmap = {};
            let anyCached = false;
            results.forEach((r, idx) => {
                byDate[dates[idx]] = r.services || [];
                for (const cl of (r.clients || [])) cmap[cl.id] = cl;
                if (r.cached) anyCached = true;
            });
            setRoutesByDate(byDate);
            setClients(cmap);
            setCached(anyCached);
        } catch {
            toast.error("No se pudo cargar la ruta");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // Fetch pending supply transfer count
    useEffect(() => {
        api.get("/supplies/transfers/pending-count")
            .then(res => setPendingSupplies(res.data.count || 0))
            .catch(() => {});
        api.get("/equipment/transfers/pending-count")
            .then(res => setPendingEquipment(res.data.count || 0))
            .catch(() => {});
        api.get("/time-entries/today")
            .then(res => setTimeEntry(res.data))
            .catch(() => {});
    }, []);

    const performStart = async (svc, locationPayload) => {
        setStartingId(svc.id);
        try {
            const result = await startService(svc.id, locationPayload || {});
            const updated = result?.service || result || {};
            setRoutesByDate((prev) => ({
                ...prev,
                [today]: (prev[today] || []).map((x) => (x.id === svc.id ? { ...x, ...updated } : x)),
            }));
            if (locationPayload && locationPayload.latitude != null) {
                const distance = updated.start_distance_to_client_m;
                if (distance != null && distance > 200) {
                    toast.warning(`Iniciado a las ${formatTime(updated.entry_time)} — Estás a ${formatDistance(distance)} del cliente`);
                } else {
                    toast.success(`Servicio iniciado a las ${formatTime(updated.entry_time)} — Ubicación registrada${distance != null ? ` (${formatDistance(distance)} del cliente)` : ""}`);
                }
            } else {
                toast.warning(`Iniciado a las ${formatTime(updated.entry_time)} — No se registró ubicación`);
            }
            nav(`/tech/service/${svc.id}`);
        } catch {
            toast.error("No se pudo iniciar el servicio");
        } finally {
            setStartingId(null);
            setLocating(false);
            setFallbackOpen(false);
            setFallbackSvc(null);
        }
    };


    const handleCheckIn = async () => {
        setCheckingIn(true);
        const body = {};
        try {
            const geo = await getCurrentLocation({ timeoutMs: 12000, enableHighAccuracy: true });
            if (geo.ok) { body.latitude = geo.latitude; body.longitude = geo.longitude; body.accuracy_m = geo.accuracy_m; }
        } catch { /* GPS failed — continue without */ }
        try {
            const res = await api.post("/time-entries/check-in", body);
            setTimeEntry(res.data);
            toast.success("Entrada registrada");
        } catch (err) { toast.error(err?.response?.data?.detail || "Error al registrar entrada"); }
        finally { setCheckingIn(false); }
    };

    const handleCheckOut = async () => {
        setCheckingOut(true);
        const body = {};
        try {
            const geo = await getCurrentLocation({ timeoutMs: 12000, enableHighAccuracy: true });
            if (geo.ok) { body.latitude = geo.latitude; body.longitude = geo.longitude; body.accuracy_m = geo.accuracy_m; }
        } catch { /* GPS failed — continue without */ }
        try {
            const res = await api.post("/time-entries/check-out", body);
            setTimeEntry(res.data);
            toast.success("Salida registrada");
        } catch (err) { toast.error(err?.response?.data?.detail || "Error al registrar salida"); }
        finally { setCheckingOut(false); }
    };

    const handleStart = async (e, svc) => {
        e.preventDefault();
        e.stopPropagation();
        if (startingId || locating) return;
        setLocating(true);
        setStartingId(svc.id);
        const geo = await getCurrentLocation({ timeoutMs: 12000, enableHighAccuracy: true });
        if (geo.ok) {
            await performStart(svc, {
                latitude: geo.latitude,
                longitude: geo.longitude,
                accuracy_m: geo.accuracy_m,
            });
        } else {
            // Show modal asking whether to continue without location
            const reason = geo.error === "denied"
                ? "Permiso de ubicación denegado por el navegador o el dispositivo."
                : geo.error === "timeout"
                    ? "El GPS no respondió a tiempo. Verifica que tu ubicación esté activa."
                    : geo.error === "unavailable"
                        ? "No se pudo obtener una señal de GPS en este momento."
                        : geo.error === "unsupported"
                            ? "Este dispositivo no soporta geolocalización."
                            : (geo.errorMessage || "Error desconocido al capturar GPS.");
            setFallbackErrorMsg(reason);
            setFallbackSvc(svc);
            setFallbackOpen(true);
            setStartingId(null);
            setLocating(false);
        }
    };

    const handleRetryGps = async () => {
        if (!fallbackSvc) return;
        const svc = fallbackSvc;
        setFallbackOpen(false);
        // Re-trigger main flow
        await handleStart({ preventDefault() {}, stopPropagation() {} }, svc);
    };

    const handleContinueWithoutGps = async () => {
        if (!fallbackSvc) return;
        await performStart(fallbackSvc, null);
    };

    const handleVoyTarde = (e, svc, client) => {
        e.preventDefault();
        e.stopPropagation();
        setTardySvc(svc);
        setTardyClient(client);
        setTardyTime(TARDY_TIMES[0]);
        setTardyOpen(true);
    };

    const handleSendVoyTarde = () => {
        const phone = normalizePhone(tardyClient?.whatsapp || tardyClient?.celular);
        if (!phone) return toast.error("No hay número de WhatsApp disponible");
        const msg = buildTardyMessage(
            tardyClient?.contact_name || tardyClient?.name || "Cliente",
            user?.full_name || "Técnico",
            tardyTime
        );
        const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
        window.open(url, "_blank");
        setTardyOpen(false);
        toast.success("Abriendo WhatsApp...");
    };

    const goToDetail = (svcId) => nav(`/tech/service/${svcId}`);

    const isToday = selectedDate === today;
    const services = routesByDate[selectedDate] || [];
    const todayServices = routesByDate[today] || [];
    const counts = dates.reduce((acc, d) => { acc[d] = (routesByDate[d] || []).length; return acc; }, {});
    const total = todayServices.length;
    const completed = todayServices.filter((s) => s.status === "completed").length;
    const progress = total ? Math.round((completed / total) * 100) : 0;

    return (
        <div className="px-4 pt-4 pb-6">
            {/* Header */}
            <div className="flex items-end justify-between mb-3">
                <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Mi Ruta del Día</div>
                    <h1 className="text-2xl font-semibold capitalize" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                        {format(new Date(selectedDate + "T00:00:00"), "EEEE d 'de' MMMM", { locale: es })}
                    </h1>
                </div>
                <button onClick={load} className="p-2 rounded-lg hover:bg-secondary" data-testid="tech-refresh-button" title="Actualizar">
                    <RotateCw className={`w-5 h-5 text-muted-foreground ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            {cached && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 mb-3 text-xs text-amber-900 flex items-center gap-2" data-testid="cached-route-banner">
                    <CloudOff className="w-3.5 h-3.5" />
                    Mostrando ruta guardada en el dispositivo. Reconéctate para actualizar.
                </div>
            )}

            {/* Progress bar */}
            <div className="rounded-xl border bg-white p-3 mb-4 shadow-sm" data-testid="tech-progress">
                <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-muted-foreground">Progreso del día</span>
                    <span className="font-mono tabular-nums font-semibold">{completed}/{total}</span>
                </div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-[hsl(var(--accent))] transition-all duration-500"
                         style={{ width: `${progress}%` }} />
                </div>
            </div>

            {/* Attendance / Time Entry Card */}
            <div className="mb-4 rounded-xl border bg-white p-4 shadow-sm" data-testid="time-entry-card">
                <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Marcaje de Asistencia</span>
                </div>
                {!timeEntry || !timeEntry.check_in ? (
                    <button
                        onClick={handleCheckIn}
                        disabled={checkingIn}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors disabled:opacity-60"
                        data-testid="check-in-btn"
                    >
                        <LogIn className="w-5 h-5" />
                        {checkingIn ? "Registrando..." : "Marcar Entrada"}
                    </button>
                ) : !timeEntry.check_out ? (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                <span className="text-sm font-medium">Entrada registrada</span>
                            </div>
                            <span className="text-sm font-mono tabular-nums">
                                {new Date(timeEntry.check_in).toLocaleTimeString("es-SV", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                            </span>
                        </div>
                        {timeEntry.check_in_latitude && (
                            <a href={`https://www.google.com/maps?q=${timeEntry.check_in_latitude},${timeEntry.check_in_longitude}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 underline flex items-center gap-0.5">
                                <MapPin className="w-2.5 h-2.5" /> Ver ubicación de entrada
                            </a>
                        )}
                        <button
                            onClick={handleCheckOut}
                            disabled={checkingOut}
                            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors disabled:opacity-60"
                            data-testid="check-out-btn"
                        >
                            <LogOutIcon className="w-5 h-5" />
                            {checkingOut ? "Registrando..." : "Marcar Salida"}
                        </button>
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Entrada</span>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-mono tabular-nums font-medium">
                                    {new Date(timeEntry.check_in).toLocaleTimeString("es-SV", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                </span>
                                {timeEntry.check_in_latitude && (
                                    <a href={`https://www.google.com/maps?q=${timeEntry.check_in_latitude},${timeEntry.check_in_longitude}`} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                                        <MapPin className="w-3 h-3" />
                                    </a>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Salida</span>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-mono tabular-nums font-medium">
                                    {new Date(timeEntry.check_out).toLocaleTimeString("es-SV", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                </span>
                                {timeEntry.check_out_latitude && (
                                    <a href={`https://www.google.com/maps?q=${timeEntry.check_out_latitude},${timeEntry.check_out_longitude}`} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                                        <MapPin className="w-3 h-3" />
                                    </a>
                                )}
                            </div>
                        </div>
                        <div className="text-[10px] text-center text-muted-foreground pt-1">Jornada completada</div>
                    </div>
                )}
            </div>

            {/* Supplies Management Button */}
            <button
                onClick={() => nav("/tech/supplies")}
                className="w-full mb-4 rounded-xl border bg-white p-3 shadow-sm flex items-center gap-3 hover:bg-secondary/50 transition-colors active:scale-[0.99]"
                data-testid="tech-supplies-btn"
            >
                <div className="w-10 h-10 rounded-lg bg-[#FFD62B]/20 flex items-center justify-center shrink-0">
                    <Package className="w-5 h-5 text-[#FFD62B]" strokeWidth={2.2} />
                </div>
                <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-semibold">Gestión de Insumos</div>
                    <div className="text-xs text-muted-foreground">Ver inventario y trasladar productos</div>
                </div>
                {pendingSupplies > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shrink-0">
                        {pendingSupplies}
                    </span>
                )}
                <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
            </button>

            {/* Equipment Management Button */}
            <button
                onClick={() => nav("/tech/equipment")}
                className="w-full mb-4 rounded-xl border bg-white p-3 shadow-sm flex items-center gap-3 hover:bg-secondary/50 transition-colors active:scale-[0.99]"
                data-testid="tech-equipment-btn"
            >
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                    <Wrench className="w-5 h-5 text-blue-600" strokeWidth={2.2} />
                </div>
                <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-semibold">Gestión de Equipos</div>
                    <div className="text-xs text-muted-foreground">Equipos asignados y traslados</div>
                </div>
                {pendingEquipment > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shrink-0">
                        {pendingEquipment}
                    </span>
                )}
                <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
            </button>


            {/* Services list (timeline) */}
            <DayTabs value={selectedDate} onChange={setSelectedDate} counts={counts} />
            <div className="relative" data-testid="tech-rad-list">
                {loading ? (
                    <div className="space-y-3">
                        {[1,2,3].map((i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
                    </div>
                ) : services.length === 0 ? (
                    <Card className="p-8 text-center">
                        <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">{isToday ? "No hay servicios programados para hoy." : "No hay servicios programados para este día."}</p>
                    </Card>
                ) : (
                    <div className="relative pl-7 space-y-3 before:content-[''] before:absolute before:left-3 before:top-2 before:bottom-2 before:w-px before:bg-border">
                        {services.map((s) => {
                            const c = clients[s.client_id] || {};
                            const supportCount = (s.support_technician_ids || []).length;
                            const t = computeServiceTimes(s.entry_time, s.exit_time, supportCount);
                            const isCompleted = s.status === "completed";
                            const isInProgress = s.status === "in_progress";
                            const isPending = s.status === "pending";
                            const isStarting = startingId === s.id;

                            return (
                                <div
                                    key={s.id}
                                    className="block group cursor-pointer"
                                    onClick={() => goToDetail(s.id)}
                                    data-testid={`tech-rad-item-${s.id}`}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === "Enter") goToDetail(s.id); }}
                                >
                                    <div className="absolute -left-0 mt-3 w-6 h-6 rounded-full bg-white border-2 border-[hsl(var(--trulyn-navy))] flex items-center justify-center">
                                        <span className={`w-1.5 h-1.5 rounded-full ${isCompleted ? "bg-green-600" : isInProgress ? "bg-[hsl(var(--accent))]" : "bg-zinc-300"}`} />
                                    </div>
                                    <Card className="p-4 hover:shadow-md transition-shadow border-border/70 group-active:scale-[0.99]">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                    <span className="font-mono text-lg font-semibold text-[hsl(var(--trulyn-navy))]">{s.scheduled_time}</span>
                                                    <StatusPill status={s.status} />
                                                    {s._sync_pending && (
                                                        <span className="pill pill-in_progress" title="Pendiente de sincronizar" data-testid={`pending-sync-${s.id}`}>
                                                            <RefreshCw className="w-3 h-3" />
                                                            Pendiente sync
                                                        </span>
                                                    )}
                                                </div>
                                                <h3 className="font-semibold text-base truncate">{c.name || "Cliente"}</h3>
                                                {s.service_type_flag && (
                                                    <p className="text-xs font-medium text-[hsl(var(--accent-foreground))] bg-[hsl(var(--accent))]/15 rounded px-1.5 py-0.5 inline-block mt-0.5">{s.service_type_flag}</p>
                                                )}
                                                <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                                                    <MapPin className="w-3.5 h-3.5 inline mr-1" />
                                                    {c.address || c.city || "—"}
                                                </p>
                                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                                    {s.order_number && <Badge variant="outline" className="font-mono">{s.order_number}</Badge>}
                                                    {s.invoice_type && <Badge variant="outline">{s.invoice_type}</Badge>}
                                                    <span className="text-xs font-semibold">${(s.service_value || 0).toFixed(2)}</span>
                                                </div>
                                                {s.instructions && (
                                                    <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                                                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                                        <span className="line-clamp-2">{s.instructions}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-0.5 transition-transform shrink-0 mt-1" />
                                        </div>

                                        {/* Time / action row */}
                                        {(isToday || !isPending) && (
                                        <div className="mt-3 pt-3 border-t flex items-center gap-2 flex-wrap">
                                            {isPending && (
                                                <div className="flex flex-col gap-2 w-full">
                                                    <Button
                                                        onClick={(e) => handleStart(e, s)}
                                                        disabled={isStarting || locating}
                                                        className="h-10 px-4 bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold w-full"
                                                        data-testid={`tech-rad-start-${s.id}`}
                                                    >
                                                        {isStarting && locating ? (
                                                            <><Crosshair className="w-4 h-4 mr-1.5 animate-pulse" /> Obteniendo ubicación...</>
                                                        ) : isStarting ? (
                                                            <><Play className="w-4 h-4 mr-1.5 fill-black" /> Iniciando...</>
                                                        ) : (
                                                            <><Play className="w-4 h-4 mr-1.5 fill-black" /> Iniciar servicio</>
                                                        )}
                                                    </Button>
                                                    {(c.celular || c.whatsapp) && (
                                                        <Button
                                                            onClick={(e) => handleVoyTarde(e, s, c)}
                                                            variant="outline"
                                                            className="h-10 px-4 border-[#FF0000] text-[#FF0000] hover:bg-[#FF0000]/5 font-semibold w-full"
                                                            data-testid={`tech-rad-voy-tarde-${s.id}`}
                                                        >
                                                            <MessageCircle className="w-4 h-4 mr-1.5" /> Voy Tarde
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                            {isInProgress && (
                                                <>
                                                    <div className="flex items-center gap-1.5 text-xs bg-[#FFD62B]/20 px-2.5 py-1.5 rounded-md font-medium">
                                                        <Clock className="w-3.5 h-3.5" />
                                                        Inicio: <span className="font-mono font-semibold">{formatTime(s.entry_time)}</span>
                                                    </div>
                                                    {supportCount > 0 && (
                                                        <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`tech-rad-support-${s.id}`}>
                                                            <Users className="w-3.5 h-3.5" />
                                                            +{supportCount} apoyo
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {isCompleted && t.durationMinutes !== null && (
                                                <>
                                                    <div className="flex items-center gap-1.5 text-xs bg-green-50 border border-green-200 text-green-800 px-2.5 py-1.5 rounded-md font-medium">
                                                        <Clock className="w-3.5 h-3.5" />
                                                        {formatTime(s.entry_time)} → {formatTime(s.exit_time)}
                                                        <span className="font-mono font-semibold ml-1">({t.durationLabel})</span>
                                                    </div>
                                                    {t.totalTechnicians > 1 && (
                                                        <div className="flex items-center gap-1 text-xs text-muted-foreground" title={`${t.totalTechnicians} técnicos × ${t.durationLabel}`}>
                                                            <Users className="w-3.5 h-3.5" />
                                                            <span className="font-mono font-semibold">{t.manHoursLabel}</span> hombre
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {s.status === "not_done" && (
                                                <div className="text-xs text-muted-foreground italic">Servicio no realizado</div>
                                            )}
                                        </div>
                                        )}
                                    </Card>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Fallback dialog when GPS fails */}
            <Dialog open={fallbackOpen} onOpenChange={(o) => { if (!o) { setFallbackOpen(false); setFallbackSvc(null); } }}>
                <DialogContent data-testid="gps-fallback-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <MapPinOff className="w-5 h-5 text-[#FF0000]" />
                            No se registró ubicación
                        </DialogTitle>
                        <DialogDescription className="pt-2 text-sm leading-relaxed">
                            {fallbackErrorMsg}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                        Puedes reintentar (recomendado) o iniciar el servicio sin ubicación. El sistema dejará constancia de que no se capturó GPS.
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
                        <Button
                            variant="outline"
                            onClick={() => { setFallbackOpen(false); setFallbackSvc(null); }}
                            className="w-full sm:w-auto"
                            data-testid="gps-fallback-cancel"
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleContinueWithoutGps}
                            className="w-full sm:w-auto border-zinc-300"
                            data-testid="gps-fallback-skip"
                        >
                            Iniciar sin ubicación
                        </Button>
                        <Button
                            onClick={handleRetryGps}
                            className="w-full sm:w-auto bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold"
                            data-testid="gps-fallback-retry"
                        >
                            <Crosshair className="w-4 h-4 mr-1.5" />
                            Reintentar GPS
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Voy Tarde dialog */}
            <Dialog open={tardyOpen} onOpenChange={(o) => { if (!o) setTardyOpen(false); }}>
                <DialogContent data-testid="voy-tarde-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-[#FF0000]">
                            <MessageCircle className="w-5 h-5" />
                            Voy Tarde — Avisar al cliente
                        </DialogTitle>
                        <DialogDescription className="text-sm leading-relaxed">
                            Se abrirá WhatsApp con un mensaje personalizado para <strong>{tardyClient?.name}</strong>.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 py-1">
                        <div>
                            <label className="text-sm font-medium mb-1.5 block">Tiempo estimado de llegada</label>
                            <Select value={tardyTime} onValueChange={setTardyTime}>
                                <SelectTrigger className="h-11" data-testid="voy-tarde-time-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {TARDY_TIMES.map((t) => (
                                        <SelectItem key={t} value={t} data-testid={`voy-tarde-option-${t.replace(" ", "-")}`}>{t}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="rounded-lg bg-zinc-50 border px-3 py-2.5 text-xs text-foreground/80 leading-relaxed whitespace-pre-line">
                            {tardyClient && user
                                ? buildTardyMessage(tardyClient.contact_name || tardyClient.name, user.full_name, tardyTime)
                                : ""}
                        </div>
                    </div>

                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setTardyOpen(false)}
                            className="w-full sm:w-auto"
                            data-testid="voy-tarde-cancel"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSendVoyTarde}
                            className="w-full sm:w-auto bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold"
                            data-testid="voy-tarde-send"
                        >
                            <MessageCircle className="w-4 h-4 mr-1.5" />
                            Enviar por WhatsApp
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
